/**
 * RS-4 — the submittal register: what the firm expects from each party.
 *
 * Written at the hire and worked by hand afterwards. The view is a list, not a
 * form: every line says what it is for, what is expected, from whom and when,
 * and where it came from. The title and the due date are edited where they sit.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Send } from 'lucide-react';
import { toast } from 'sonner';
import DockPanel from '@/components/shell/DockPanel';
import '@/styles/submittal-register.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [
  ['shop_drawing', 'Shop drawing'],
  ['sample', 'Sample'],
  ['product_data', 'Product data'],
  ['test_report', 'Test report'],
  ['certification', 'Certification'],
];

const STATE_FACE = {
  expected: ['Expected', 'neutral'],
  spec_stands: ['Spec sheet stands', 'ok'],
  received: ['Received', 'wait'],
  under_review: ['Under review', 'wait'],
  approved: ['Approved', 'ok'],
  approved_as_noted: ['Approved as noted', 'ok'],
  revise: ['Revise & resubmit', 'info'],
  rejected: ['Rejected', 'late'],
  released: ['Released', 'ok'],
  // RS-7b — a line the firm raised by issuing its own shop drawing.
  sent_for_confirmation: ['Sent for confirmation', 'wait'],
  confirmed: ['Confirmed', 'ok'],
  returned: ['Returned with marks', 'info'],
  accepted: ['Accepted by both', 'ok'],
};

const fmtDay = (iso) => (iso
  ? new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  : '—');

const cameFrom = (line) => {
  if (line.source?.kind === 'hire') {
    return `${line.source.package_number || 'a package'} · hired ${fmtDay(String(line.source.hired_at || '').slice(0, 10))}`;
  }
  if (line.source?.kind === 'issued') return `issued by us · ${fmtDay(String(line.created_at || '').slice(0, 10))}`;
  return 'added by hand';
};

const forLabel = (line) => {
  const f = line.for || {};
  if (f.category_key) return f.category_key;
  if ((f.sheet_ids || []).length) return (f.sheet_ids || [])[0];
  return '—';
};

export const AddLinePanel = ({ projectId, parties, onClose, onSave }) => {
  const [people, setPeople] = useState(parties);
  const [form, setForm] = useState({
    membership_id: parties[0]?.membership_id || '', kind: 'shop_drawing',
    title: '', due_date: '', category_key: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // A register with nothing on it yet still has parties on the project.
  useEffect(() => {
    if (parties.length) return;
    axios.get(`${API}/projects/${projectId}/memberships`)
      .then((r) => {
        const rows = (r.data?.memberships || r.data?.rows || []).map((m) => ({
          membership_id: m.id,
          party_name: m.company_name || m.name || m.email || 'This party',
        }));
        setPeople(rows);
        setForm((f) => ({ ...f, membership_id: f.membership_id || rows[0]?.membership_id || '' }));
      })
      .catch(() => {});
  }, [projectId, parties.length]);

  const save = async () => {
    if (!form.membership_id || !form.title.trim()) {
      toast.error('Say who it is from and what you expect');
      return;
    }
    setBusy(true);
    try {
      await onSave({
        membership_id: form.membership_id, kind: form.kind, title: form.title.trim(),
        due_date: form.due_date || undefined,
        for: { category_key: form.category_key.trim().toUpperCase() || null },
      });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add that line');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DockPanel onClose={onClose} width="360px">
      <div className="krg-panel" data-testid="register-add-panel">
        <div className="krg-panel__hd">
          <span className="krg-k">Register</span>
          <h2>Add a line</h2>
        </div>
        <label className="krg-f">From
          <select value={form.membership_id} onChange={set('membership_id')}
            data-testid="register-add-party">
            {people.map((p) => (
              <option key={p.membership_id} value={p.membership_id}>{p.party_name}</option>
            ))}
          </select>
        </label>
        <label className="krg-f">Kind
          <select value={form.kind} onChange={set('kind')} data-testid="register-add-kind">
            {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="krg-f">For (spec category)
          <input value={form.category_key} onChange={set('category_key')}
            placeholder="CAB" data-testid="register-add-for" />
        </label>
        <label className="krg-f">What we expect
          <input value={form.title} onChange={set('title')}
            placeholder="Island shop drawing" data-testid="register-add-title" />
        </label>
        <label className="krg-f">Due
          <input type="date" value={form.due_date} onChange={set('due_date')}
            data-testid="register-add-due" />
        </label>
        <div className="krg-panel__ft">
          <button type="button" className="krg-btn" onClick={onClose}
            data-testid="register-add-cancel">Cancel</button>
          <button type="button" className="krg-btn brand" disabled={busy} onClick={save}
            data-testid="register-add-save">{busy ? 'Adding…' : 'Add the line'}</button>
        </div>
      </div>
    </DockPanel>
  );
};

export default function SubmittalRegister({ projectId, reloadKey = 0, submittals = [], onOpen }) {
  const [lines, setLines] = useState([]);
  const [counts, setCounts] = useState({ expected: 0, in: 0, approved: 0 });
  const [parties, setParties] = useState([]);
  const [who, setWho] = useState('all');
  const [dir, setDir] = useState('all');
  const [adding, setAdding] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/submittal-register`);
      setLines(data.lines || []);
      setCounts(data.counts || {});
      setParties(data.parties || []);
    } catch {
      /* an empty register is the common case; the page says so below */
    }
  }, [projectId]);

  // RS-5 — and again when a hand-in is picked up at the desk.
  useEffect(() => { load(); }, [load, reloadKey]);

  const shown = useMemo(() => lines.filter((l) =>
    (who === 'all' || l.membership_id === who)
    && (dir === 'all' || (dir === 'issued') === (l.source?.kind === 'issued'))), [lines, who, dir]);

  const patch = async (line, fields) => {
    try {
      const { data } = await axios.patch(
        `${API}/projects/${projectId}/submittal-register/${line.id}`, fields);
      setLines((rows) => rows.map((r) => (r.id === line.id ? data.line : r)));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save that');
      load();
    }
  };

  const addLine = async (body) => {
    await axios.post(`${API}/projects/${projectId}/submittal-register`, body);
    await load();
    toast.success('Line added');
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/submittal-register/send`, {});
      toast.success(data.sent
        ? `Sent ${data.sent} line${data.sent === 1 ? '' : 's'} to ${data.parties} ${data.parties === 1 ? 'party' : 'parties'}`
        : 'Every line has already been sent');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send the list');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="krg" data-testid="rfi-register">
      <header className="krg-hd">
        <div>
          <h2>Submittal register</h2>
          <p className="krg-k" data-testid="register-counts">
            {counts.expected || 0} expected · {counts.in || 0} in · {counts.approved || 0} approved
          </p>
        </div>
        <div className="krg-hd__acts">
          <button type="button" className="krg-btn" onClick={() => setAdding(true)}
            data-testid="register-add">
            <Plus className="w-3 h-3" /> Add a line
          </button>
          <button type="button" className="krg-btn brand" disabled={sending} onClick={send}
            data-testid="register-send">
            <Send className="w-3 h-3" /> {sending ? 'Sending…' : 'Send the list to the parties'}
          </button>
        </div>
      </header>

      {lines.length ? (
        <div className="krg-chips">
          <button type="button" className={`krg-chip${who === 'all' ? ' on' : ''}`}
            onClick={() => setWho('all')} data-testid="register-chip-all">
            All <span>{lines.length}</span>
          </button>
          {parties.map((p) => (
            <button type="button" key={p.membership_id}
              className={`krg-chip${who === p.membership_id ? ' on' : ''}`}
              onClick={() => setWho(p.membership_id)}
              data-testid={`register-chip-${p.membership_id}`}>
              {p.party_name} <span>{p.expected + p.in + p.approved + (p.spec_stands || 0)}</span>
            </button>
          ))}
          <span className="krg-chips__gap" aria-hidden="true" />
          <button type="button" className={`krg-chip${dir === 'issued' ? ' on' : ''}`}
            onClick={() => setDir(dir === 'issued' ? 'all' : 'issued')} data-testid="register-chip-issued">
            Issued by us <span>{lines.filter((l) => l.source?.kind === 'issued').length}</span>
          </button>
          <button type="button" className={`krg-chip${dir === 'theirs' ? ' on' : ''}`}
            onClick={() => setDir(dir === 'theirs' ? 'all' : 'theirs')} data-testid="register-chip-theirs">
            Expected or handed in <span>{lines.filter((l) => l.source?.kind !== 'issued').length}</span>
          </button>
        </div>
      ) : null}

      {shown.length ? (
        <div className="krg-rows">
          <div className="krg-row krg-row--hd">
            <span>For</span><span>What we expect</span><span>Kind</span>
            <span>From</span><span>Due</span><span>Came from</span><span>State</span><span />
          </div>
          {shown.map((line) => {
            const [label, tone] = STATE_FACE[line.state] || STATE_FACE.expected;
            return (
              <div className="krg-row" key={line.id} data-testid={`register-row-${line.id}`}>
                <span className="krg-for">{forLabel(line)}</span>
                <span>
                  <input className="krg-in" defaultValue={line.title}
                    onBlur={(e) => (e.target.value.trim() && e.target.value !== line.title
                      ? patch(line, { title: e.target.value.trim() }) : null)}
                    data-testid={`register-title-${line.id}`} />
                </span>
                <span className="krg-kind">{line.kind_label}</span>
                <span className="krg-from">{line.party_name}</span>
                <span>
                  <input className="krg-in krg-in--day" type="date"
                    defaultValue={line.due_date || ''}
                    onChange={(e) => patch(line, { due_date: e.target.value })}
                    data-testid={`register-due-${line.id}`} />
                </span>
                <span className="krg-src">
                  {cameFrom(line)}
                  {line.sent_at ? (
                    <em data-testid={`register-sent-${line.id}`}>sent</em>
                  ) : null}
                </span>
                <span>
                  <b className={`krg-pill ${tone}`} data-testid={`register-state-${line.id}`}>
                    {label}
                  </b>
                </span>
                <span className="krg-open">
                  {line.submittal_id ? (
                    <button type="button" className="krg-btn brand"
                      onClick={() => {
                        const s = submittals.find((x) => x.id === line.submittal_id);
                        if (s && onOpen) onOpen(s);
                      }}
                      data-testid={`register-open-${line.id}`}>Open</button>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="krg-empty" data-testid="register-empty">
          Nothing is expected yet. Hiring from a bid package writes the list, or add a line by hand.
        </p>
      )}

      {adding ? (
        <AddLinePanel projectId={projectId} parties={parties}
          onClose={() => setAdding(false)} onSave={addLine} />
      ) : null}
    </section>
  );
}
